// Timeline de eventos de uma cobrança: quem emitiu, quem cancelou, quem reemitiu,
// quem enviou, quando pagou — na ordem em que aconteceu.
//
// Por que isso existe (chamado do Ítalo, 28/08/2026): `emitido_por_nome`/`emitido_em`
// descrevem o boleto que está VIVO e são sobrescritos na reemissão. Isso é de
// propósito desde 19/08/2026 (o modal mostrava o nome de quem emitiu semanas antes
// num boleto que já era de outra pessoa), mas fez o trabalho anterior sumir da tela:
// o único registro de quem emitiu primeiro era o `system_logs`, cujo RLS de SELECT é
// só 'adm' — o analista não conseguia ver a própria emissão.
//
// Desde então a fatura carrega `historico_emissoes` (append-only, escrito pelo
// gatilho trg_faturas_historico_emissoes), simétrico ao `historico_cancelamentos`
// que já existia. Este módulo junta os dois com os carimbos do envio e do pagamento.
//
// ⚠️ Relógios: `emitido_em` e `cancelado_em` vêm do `new Date()` do NAVEGADOR de quem
// clicou, não do servidor. Em 26/08/2026 a máquina do João estava ~2min adiantada e a
// da Dhaiane ~3min — o suficiente para um cancelamento parecer posterior à reemissão
// que veio depois dele. Por isso cada evento carrega `ordem` (o carimbo do servidor,
// quando existe) separado de `em` (o que se mostra na tela).
//
// Pulo de cálculo e pulo de envio entraram em 07/10/2026 (pergunta do Ítalo sobre a UC
// 345243701247): o mês sem boleto aparecia só como "Cobrança enviada ao cliente", sem
// dizer que o cálculo foi pulado, por quem e por quê — e o dado já estava na fatura.

export type EventoCobrancaTipo =
  | 'pulo_calculo'
  | 'emissao'
  | 'reemissao'
  | 'cancelamento'
  | 'envio'
  | 'comunicado'
  | 'pulo_envio'
  | 'pagamento';

export interface EventoCobranca {
  tipo: EventoCobrancaTipo;
  /** ISO exibido na tela. Pode vir do relógio do navegador de quem agiu. */
  em: string;
  /** Epoch ms usado só para ordenar — prefere o carimbo do servidor. */
  ordem: number;
  por: string;
  /** Motivo do cancelamento ou do pulo; nada nos demais tipos. */
  detalhe?: string;
  numCobranca?: string | null;
}

export interface EmissaoRegistrada {
  emitido_em?: string | null;
  emitido_por_nome?: string | null;
  num_cobranca_boleto?: string | null;
  /** now() do servidor no momento em que o gatilho gravou a entrada. */
  registrado_em?: string | null;
  origem?: string | null;
}

export interface CancelamentoRegistrado {
  cancelado_em?: string | null;
  cancelado_por_nome?: string | null;
  motivo_cancelamento_boleto?: string | null;
  /** now() do servidor, posto por trg_faturas_carimbo_cancelamento. */
  registrado_em?: string | null;
}

export interface FaturaParaTimeline {
  historico_emissoes?: EmissaoRegistrada[] | null;
  historico_cancelamentos?: CancelamentoRegistrado[] | null;
  emitido_em?: string | null;
  emitido_por_nome?: string | null;
  num_cobranca_boleto?: string | null;
  cancelado_em?: string | null;
  cancelado_por_nome?: string | null;
  motivo_cancelamento_boleto?: string | null;
  enviado_em?: string | null;
  enviado_por_nome?: string | null;
  data_pgto_boleto?: string | null;
  pular_calculo_em?: string | null;
  pular_calculo_por_nome?: string | null;
  motivo_pular_calculo?: string | null;
  pular_envio_em?: string | null;
  pular_envio_por_nome?: string | null;
  motivo_pular_envio?: string | null;
}

const SEM_NOME = 'Sistema';

/** `enviado_em` e `pular_envio_em` saem do mesmo update, com ms de diferença. */
const MESMO_CLIQUE_MS = 60_000;

const ehData = (valor?: string | null): valor is string =>
  !!valor && !Number.isNaN(new Date(valor).getTime());

const ms = (valor: string) => new Date(valor).getTime();

/**
 * Emissões da fatura em ordem cronológica.
 *
 * O fallback para `emitido_em`/`emitido_por_nome` cobre a fatura que for emitida
 * antes de o gatilho existir na base local, ou o objeto parcial que alguma tela
 * monta sem trazer a coluna nova — nunca deve sobrar em produção, onde o backfill
 * de 28/08/2026 fechou as 5.717 faturas já emitidas.
 */
export function emissoesDaFatura(fatura: FaturaParaTimeline): EmissaoRegistrada[] {
  const registradas = (fatura.historico_emissoes ?? []).filter(e => ehData(e?.emitido_em));

  if (registradas.length > 0) return registradas;
  if (!ehData(fatura.emitido_em)) return [];

  return [{
    emitido_em: fatura.emitido_em,
    emitido_por_nome: fatura.emitido_por_nome ?? null,
    num_cobranca_boleto: fatura.num_cobranca_boleto ?? null,
    registrado_em: fatura.emitido_em,
    origem: 'fatura',
  }];
}

/** Quem emitiu o boleto pela primeira vez — o dado que a reemissão sobrescrevia. */
export function primeiraEmissao(fatura: FaturaParaTimeline): EmissaoRegistrada | null {
  return emissoesDaFatura(fatura)[0] ?? null;
}

/**
 * Cancelamentos da fatura.
 *
 * `cancelado_em`/`cancelado_por_nome` continuam sendo resíduo do boleto anterior de
 * propósito (a UI separa "cancelado agora" por `status_boleto_vigor`), então só
 * entram aqui quando o histórico ainda não os contém — 53 faturas nunca ganharam
 * entrada no array e outras 78 têm um `cancelado_em` que ficou de fora dele.
 */
export function cancelamentosDaFatura(fatura: FaturaParaTimeline): CancelamentoRegistrado[] {
  const registrados = (fatura.historico_cancelamentos ?? []).filter(c => ehData(c?.cancelado_em));

  if (!ehData(fatura.cancelado_em)) return registrados;
  if (registrados.some(c => ms(c.cancelado_em as string) === ms(fatura.cancelado_em as string))) {
    return registrados;
  }

  return [...registrados, {
    cancelado_em: fatura.cancelado_em,
    cancelado_por_nome: fatura.cancelado_por_nome ?? null,
    motivo_cancelamento_boleto: fatura.motivo_cancelamento_boleto ?? null,
  }];
}

export type TipoEnvio = 'boleto' | 'comunicado' | 'pulado';

/**
 * O que de fato saiu quando a fatura foi marcada como enviada.
 *
 * `enviado_em` é gravado pelos três botões do BoletoEmitidoModal — boleto, comunicação
 * sem cobrança e "pular envio" —, então sozinho não diz o que o cliente recebeu. Pular
 * envio grava `pular_envio_em` no mesmo update. A comunicação sai sempre que a fatura
 * tem `motivo_pular_calculo`, mesmo com boleto emitido depois do pulo: o botão escolhe
 * pelo motivo, não pelo boleto (UC 345243701247, AGO/2026).
 */
export function tipoDoEnvio(fatura: FaturaParaTimeline): TipoEnvio | null {
  if (!ehData(fatura.enviado_em)) return null;

  const enviado = ms(fatura.enviado_em);
  if (ehData(fatura.pular_envio_em) && Math.abs(enviado - ms(fatura.pular_envio_em)) <= MESMO_CLIQUE_MS) {
    return 'pulado';
  }
  // Pulo gravado depois do envio não muda o que já tinha saído.
  if (fatura.motivo_pular_calculo && (!ehData(fatura.pular_calculo_em) || ms(fatura.pular_calculo_em) <= enviado)) {
    return 'comunicado';
  }
  return 'boleto';
}

export function construirTimelineCobranca(fatura: FaturaParaTimeline | null | undefined): EventoCobranca[] {
  if (!fatura) return [];

  const eventos: EventoCobranca[] = [];

  if (ehData(fatura.pular_calculo_em)) {
    eventos.push({
      tipo: 'pulo_calculo',
      em: fatura.pular_calculo_em,
      ordem: ms(fatura.pular_calculo_em),
      por: fatura.pular_calculo_por_nome || SEM_NOME,
      detalhe: fatura.motivo_pular_calculo || undefined,
    });
  }

  emissoesDaFatura(fatura).forEach((emissao, indice) => {
    const em = emissao.emitido_em as string;
    eventos.push({
      tipo: indice === 0 ? 'emissao' : 'reemissao',
      em,
      ordem: ms(ehData(emissao.registrado_em) ? emissao.registrado_em : em),
      por: emissao.emitido_por_nome || SEM_NOME,
      numCobranca: emissao.num_cobranca_boleto ?? null,
    });
  });

  cancelamentosDaFatura(fatura).forEach(cancelamento => {
    const em = cancelamento.cancelado_em as string;
    eventos.push({
      tipo: 'cancelamento',
      em,
      ordem: ms(ehData(cancelamento.registrado_em) ? cancelamento.registrado_em : em),
      por: cancelamento.cancelado_por_nome || SEM_NOME,
      detalhe: cancelamento.motivo_cancelamento_boleto || undefined,
    });
  });

  if (ehData(fatura.pular_envio_em)) {
    eventos.push({
      tipo: 'pulo_envio',
      em: fatura.pular_envio_em,
      ordem: ms(fatura.pular_envio_em),
      por: fatura.pular_envio_por_nome || SEM_NOME,
      detalhe: fatura.motivo_pular_envio || undefined,
    });
  }

  const envio = tipoDoEnvio(fatura);
  if (envio === 'boleto' || envio === 'comunicado') {
    eventos.push({
      tipo: envio === 'boleto' ? 'envio' : 'comunicado',
      em: fatura.enviado_em as string,
      ordem: ms(fatura.enviado_em as string),
      por: fatura.enviado_por_nome || SEM_NOME,
    });
  }

  if (ehData(fatura.data_pgto_boleto)) {
    eventos.push({
      tipo: 'pagamento',
      em: fatura.data_pgto_boleto,
      ordem: ms(fatura.data_pgto_boleto),
      por: SEM_NOME,
    });
  }

  return eventos.sort((a, b) => a.ordem - b.ordem);
}
